import React from 'react';
import { Link } from 'react-router-dom';
import AdminShell from './AdminShell';
import { usePortfolio } from '../../context/PortfolioContext';
import { CONTENT_SCHEMA } from '../../content/siteContent';

const Card = ({ to, title, description, meta, children }) => (
    <Link
        to={to}
        className="block bg-gray-800 rounded-xl border border-gray-700 hover:border-primary-red p-6 transition-colors group"
    >
        <div className="flex items-start gap-4">
            <div className="text-primary-red flex-shrink-0">{children}</div>
            <div>
                <h3 className="font-poppins font-semibold text-white text-lg group-hover:text-primary-red transition-colors">
                    {title}
                </h3>
                <p className="text-gray-400 text-sm mt-1">{description}</p>
                <p className="text-gray-500 text-xs mt-3">{meta}</p>
            </div>
        </div>
    </Link>
);

const AdminDashboard = () => {
    const { projects } = usePortfolio();
    const editablePages = CONTENT_SCHEMA.length - 1; // "Site-wide" is not a page

    return (
        <AdminShell title="Knitster LDN Admin" subtitle="Manage your website">
            <main className="container mx-auto px-4 py-8">
                <h2 className="font-poppins font-light text-2xl text-white mb-6">What would you like to update?</h2>

                <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
                    <Card
                        to="/admin/portfolio"
                        title="Portfolio Manager"
                        description="Add, edit, reorder and delete portfolio projects and their images."
                        meta={`${projects.length} project${projects.length === 1 ? '' : 's'}`}
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </Card>

                    <Card
                        to="/admin/content"
                        title="Page Content"
                        description="Change any heading, paragraph, list or background image on any page."
                        meta={`${editablePages} pages + site-wide settings`}
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </Card>
                </div>

                <div className="mt-10 max-w-4xl bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                    <h3 className="font-poppins font-semibold text-white mb-2">A few tips</h3>
                    <ul className="text-gray-400 text-sm space-y-2">
                        <li>• Changes go live as soon as you press <span className="text-white">Save Changes</span>.</li>
                        <li>• Background images can be swapped from <span className="text-white">Page Content → the page → Hero</span>.</li>
                        <li>• Every page has a "Reset all pages to the original wording" option if you want to start again.</li>
                        <li>• When the badge in the header says <span className="text-yellow-400">Prototype</span>, edits are saved in this browser only - on the live site it will say <span className="text-green-400">Production</span>.</li>
                    </ul>
                </div>
            </main>
        </AdminShell>
    );
};

export default AdminDashboard;
